/**
 * Validation utilities for IrrigationPlanPage
 * Provides form validation with user-visible error messages
 */

import type { IrrigationPlanFormData } from "../../IrrigationPlanWizardPage";
import { VALIDATION_MESSAGES } from "./constants";

/**
 * Result of form validation
 */
export interface FormValidationResult {
  /** Whether the form data is valid */
  isValid: boolean;
  /** Map of field names to error messages */
  errors: Record<string, string>;
}

/**
 * Validates plan configuration form data
 * Returns validation result with Portuguese error messages
 *
 * @param data - The form data to validate
 * @returns Validation result with errors if any
 */
export function validatePlanFormData(data: IrrigationPlanFormData): FormValidationResult {
  const errors: Record<string, string> = {};

  // Validate name
  if (!data.name?.trim()) {
    errors.name = VALIDATION_MESSAGES.NAME_REQUIRED;
  }

  // Validate start time
  if (!data.startTime?.trim()) {
    errors.startTime = VALIDATION_MESSAGES.START_TIME_REQUIRED;
  }

  // Validate days of week
  if (!data.daysOfWeek || data.daysOfWeek.length === 0) {
    errors.daysOfWeek = VALIDATION_MESSAGES.DAYS_REQUIRED;
  }

  // Validate date range
  // Date rules:
  // - both empty => OK (no validity period)
  // - only one filled => OK (partial validity period)
  // - both filled => startDate <= endDate
  const hasStart = data.startDate?.trim();
  const hasEnd = data.endDate?.trim();

  if (hasStart && hasEnd && data.startDate > data.endDate) {
    errors.dateRange = VALIDATION_MESSAGES.END_DATE_BEFORE_START;
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
}

/**
 * Gets a user-friendly reason for why save is disabled
 *
 * @param planData - The current plan form data
 * @param planDataValid - Whether the plan data is valid
 * @param startValidationValid - Whether the overlap validation passed
 * @param anyProjectRunning - Whether any project is currently running
 * @returns A string explaining why save is disabled, or undefined if enabled
 */
export function getDisabledReason(
  planData: IrrigationPlanFormData,
  _planDataValid: boolean,
  startValidationValid: boolean,
  anyProjectRunning: boolean
): string | undefined {
  if (anyProjectRunning) {
    return "Não é possível salvar enquanto um projeto está em execução";
  }

  if (!planData.name?.trim()) {
    return "Digite um nome para o plano";
  }

  if (!planData.startTime?.trim()) {
    return "Selecione um horário de início";
  }

  if (!planData.daysOfWeek || planData.daysOfWeek.length === 0) {
    return "Selecione pelo menos um dia da semana";
  }

  if (planData.startDate && planData.endDate && planData.startDate > planData.endDate) {
    return "A data final deve ser posterior ou igual à data inicial";
  }

  if (planData.isEnabled && !startValidationValid) {
    return "Conflito de horário com outro plano de irrigação";
  }

  return undefined;
}
